import { Component, OnInit, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MediaService } from "../../services/media.service";
import { DiagramComponentService } from "../../services/diagram-component.service";

@Component({
  selector: "app-diagram-component-modal",
  templateUrl: "./diagram-component-modal.component.html",
  styleUrls: ["./diagram-component-modal.component.scss"],
})
export class DiagramComponentModalComponent implements OnInit {
  title: string = "Componente";
  activeTab: "pdf" | "foto" | "sinal" = "pdf";
  isAdmin: boolean = true;

  pdfFormOpen: boolean = false;
  fotoFormOpen: boolean = false;

  pdfForm: FormGroup;
  fotoForm: FormGroup;

  selectedPdfFile: File | null = null;
  selectedFotoFile: File | null = null;

  pdfs: any[] = [];
  selectedPdfIndex: number = 0;
  pdfSrc: SafeResourceUrl;
  pdfsInfoLoaded: boolean = false;
  pdfCache: { [id: string]: SafeResourceUrl } = {};

  fotos: any[] = [];
  selectedFotoIndex: number = 0;
  fotoSrc: SafeResourceUrl;
  fotosInfoLoaded: boolean = false;
  fotoCache: { [id: string]: SafeResourceUrl } = {};
  showFotoModal: boolean = false;

  sinais: any[] = [];
  selectedSinalIndex: number = 0;

  loading = {
    componenteInfo: false,
    pdfInfo: false,
    pdf: false,
    fotoInfo: false,
    foto: false,
    uploadingPdf: false,
    uploadingFoto: false,
    removingPdf: false,
    removingFoto: false,
  };

  componenteInfo: any = null;

  constructor(
    private dialogRef: MatDialogRef<DiagramComponentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sanitizer: DomSanitizer,
    private mediaService: MediaService,
    private diagramComponentService: DiagramComponentService,
    private fb: FormBuilder
  ) {
    this.pdfForm = this.fb.group({
      titulo: ["", [Validators.required]],
    });

    this.fotoForm = this.fb.group({
      titulo: ["", [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (this.data.componente && this.data.componente.nome) {
      this.title = `Componente - ${this.data.componente.nome}`;
    }

    this.loadComponenteInfo();
  }

  togglePdfForm(): void {
    this.pdfFormOpen = !this.pdfFormOpen;
  }

  toggleFotoForm(): void {
    this.fotoFormOpen = !this.fotoFormOpen;
  }

  loadComponenteInfo(): void {
    this.loading.componenteInfo = true;
    this.componenteInfo = this.data.componenteInfo;

    if (this.hasPdfs()) {
      this.activeTab = "pdf";
      this.loadPdfsInfo();
    } else if (this.hasFotos()) {
      this.activeTab = "foto";
      this.loadFotosInfo();
    } else if (this.hasSinais()) {
      this.activeTab = "sinal";
      this.sinais = this.componenteInfo.associatedSignal;
    }

    this.loading.componenteInfo = false;
  }

  close(): void {
    for (const id in this.pdfCache) {
      URL.revokeObjectURL(this.pdfCache[id].toString());
    }
    for (const id in this.fotoCache) {
      URL.revokeObjectURL(this.fotoCache[id].toString());
    }

    this.dialogRef.close();
  }

  hasTabs(): boolean {
    return this.hasPdfs() || this.hasFotos() || this.hasSinais();
  }

  hasPdfs(): boolean {
    return this.componenteInfo && this.componenteInfo.pdfId && this.componenteInfo.pdfId.length > 0;
  }

  hasFotos(): boolean {
    return this.componenteInfo && this.componenteInfo.fotoId && this.componenteInfo.fotoId.length > 0;
  }

  hasSinais(): boolean {
    return this.componenteInfo && this.componenteInfo.associatedSignal && this.componenteInfo.associatedSignal.length > 0;
  }

  setActiveTab(tab: "pdf" | "foto" | "sinal"): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;

      switch (tab) {
        case "pdf":
          if (!this.pdfsInfoLoaded) {
            this.loadPdfsInfo();
          }
          break;
        case "foto":
          if (!this.fotosInfoLoaded) {
            this.loadFotosInfo();
          }
          break;
        case "sinal":
          break;
      }
    }
  }

  loadPdfsInfo(): void {
    if (!this.hasPdfs() || this.pdfsInfoLoaded) return;

    this.pdfsInfoLoaded = true;
    this.loading.pdfInfo = true;

    const requests = [];
    this.componenteInfo.pdfId.forEach((pdfId, index) => {
      this.mediaService.getPdfInfo(pdfId).subscribe(
        (response: any) => {
          if (response && response.PDFs && response.PDFs.length > 0) {
            this.pdfs.push(...response.PDFs);
            if (index === 0 && this.pdfs.length === response.PDFs.length) {
              this.selectedPdfIndex = 0;
              this.loadPdfContent(this.pdfs[0]._id);
            }
          }

          if (index === this.componenteInfo.pdfId.length - 1) {
            this.loading.pdfInfo = false;
          }
        },
        (error) => {
          console.error("Erro ao carregar informações do PDF:", error);
          this.loading.pdfInfo = false;
          if (index === 0) {
            alert("Erro: Não foi possível carregar as informações do PDF");
          }
        }
      );
    });
  }

  selectPdf(index: number): void {
    if (this.selectedPdfIndex === index) return;

    this.selectedPdfIndex = index;
    const pdfId = this.pdfs[index]._id;

    if (this.pdfCache[pdfId]) {
      this.pdfSrc = this.pdfCache[pdfId];
    } else {
      this.loadPdfContent(pdfId);
    }
  }

  get currentPdf(): any {
    return this.pdfs[this.selectedPdfIndex];
  }

  loadPdfContent(pdfId: string): void {
    if (this.pdfCache[pdfId]) {
      this.pdfSrc = this.pdfCache[pdfId];
      return;
    }

    this.loading.pdf = true;
    this.pdfSrc = null;

    this.mediaService.downloadPdf(pdfId).subscribe(
      (response: any) => {
        const blob = new Blob([response], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

        this.pdfCache[pdfId] = safeUrl;
        this.pdfSrc = safeUrl;
        this.loading.pdf = false;
      },
      (error) => {
        console.error("Erro ao baixar PDF:", error);
        this.loading.pdf = false;
        alert("Erro: Não foi possível carregar o PDF");
      }
    );
  }

  loadFotosInfo(): void {
    if (!this.hasFotos() || this.fotosInfoLoaded) return;

    this.fotosInfoLoaded = true;
    this.loading.fotoInfo = true;

    this.componenteInfo.fotoId.forEach((fotoId, index) => {
      this.mediaService.getImageInfo(fotoId).subscribe(
        (response: any) => {
          if (response && response.fotos && response.fotos.length > 0) {
            this.fotos.push(...response.fotos);

            if (index === 0 && this.fotos.length === response.fotos.length) {
              this.selectedFotoIndex = 0;
              this.loadFotoContent(this.fotos[0]._id);
            }
          }

          if (index === this.componenteInfo.fotoId.length - 1) {
            this.loading.fotoInfo = false;
          }
        },
        (error) => {
          console.error("Erro ao carregar informações da imagem:", error);
          this.loading.fotoInfo = false;
          if (index === 0) {
            alert("Erro: Não foi possível carregar as informações da imagem");
          }
        }
      );
    });
  }

  selectFoto(index: number): void {
    if (this.selectedFotoIndex === index) return;

    this.selectedFotoIndex = index;
    const fotoId = this.fotos[index]._id;

    if (this.fotoCache[fotoId]) {
      this.fotoSrc = this.fotoCache[fotoId];
    } else {
      this.loadFotoContent(fotoId);
    }
  }

  get currentFoto(): any {
    return this.fotos[this.selectedFotoIndex];
  }

  loadFotoContent(fotoId: string): void {
    if (this.fotoCache[fotoId]) {
      this.fotoSrc = this.fotoCache[fotoId];
      return;
    }

    this.loading.foto = true;
    this.fotoSrc = null;

    this.mediaService.downloadImage(fotoId).subscribe(
      (response: any) => {
        const blob = new Blob([response], { type: "image/jpeg" });
        const url = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

        this.fotoCache[fotoId] = safeUrl;
        this.fotoSrc = safeUrl;
        this.loading.foto = false;
      },
      (error) => {
        console.error("Erro ao baixar imagem:", error);
        this.loading.foto = false;
        alert("Erro: Não foi possível carregar a imagem");
      }
    );
  }

  closeFotoModal(): void {
    this.showFotoModal = false;
  }

  selectSinal(index: number): void {
    this.selectedSinalIndex = index;
  }

  get currentSinal(): any {
    return this.sinais[this.selectedSinalIndex];
  }

  onPdfFileSelected(event: any): void {
    if (event.target.files && event.target.files.length > 0) {
      this.selectedPdfFile = event.target.files[0];
    }
  }

  onFotoFileSelected(event: any): void {
    if (event.target.files && event.target.files.length > 0) {
      this.selectedFotoFile = event.target.files[0];
    }
  }

  uploadPdf(): void {
    if (this.pdfForm.invalid) {
      alert("Atenção: O título do PDF é obrigatório");
      return;
    }

    if (!this.selectedPdfFile) {
      alert("Atenção: Selecione um arquivo PDF para upload");
      return;
    }

    const filtro = {
      modulo: this.data.dadosDiagrama.modulo,
      marca: this.data.dadosDiagrama.marca,
      modelo: this.data.dadosDiagrama.modelo,
      ano: this.data.dadosDiagrama.ano,
      motor: this.data.dadosDiagrama.motor,
      sistema_injecao: this.data.dadosDiagrama.sistema_injecao,
      tipo_diagrama: this.data.dadosDiagrama.tipo_diagrama,
    };

    const formData = new FormData();
    formData.append("pdf", this.selectedPdfFile);
    formData.append("diagramaId", this.data.diagramaId);
    formData.append("jsonComponenteId", this.data.componente.id);
    formData.append("filtro", JSON.stringify(filtro));
    formData.append("titulo", this.pdfForm.value.titulo);

    this.loading.uploadingPdf = true;

    this.diagramComponentService.uploadPdf(formData).subscribe(
      (response: any) => {
        this.loading.uploadingPdf = false;

        if (response && response.code === 200) {
          this.pdfs.push({
            _id: response.PDF._id,
            titulo: response.PDF.titulo,
          });

          if (!this.componenteInfo.pdfId) {
            this.componenteInfo.pdfId = [];
          }
          this.componenteInfo.pdfId.push(response.PDF._id);

          this.pdfForm.reset();
          this.selectedPdfFile = null;

          this.selectPdf(this.pdfs.length - 1);

          this.pdfFormOpen = false;

          alert("Sucesso: PDF enviado com sucesso");
        }
      },
      (error) => {
        this.loading.uploadingPdf = false;
        alert("Erro: " + (error.error.msg || "Erro ao enviar o PDF"));
      }
    );
  }

  removePdf(index: number): void {
    if (!this.pdfs[index]) return;

    const confirmed = confirm("Deseja realmente excluir este PDF?");

    if (!confirmed) {
      return;
    }

    const pdfId = this.pdfs[index]._id;

    this.loading.removingPdf = true;

    this.diagramComponentService.removePdf(pdfId, this.data.diagramaId, this.data.componente.id).subscribe(
      (response: any) => {
        this.loading.removingPdf = false;

        if (response && response.code === 200) {
          this.pdfs.splice(index, 1);

          const pdfIdIndex = this.componenteInfo.pdfId.indexOf(pdfId);
          if (pdfIdIndex > -1) {
            this.componenteInfo.pdfId.splice(pdfIdIndex, 1);
          }

          if (this.pdfs.length > 0) {
            this.selectedPdfIndex = 0;
            this.loadPdfContent(this.pdfs[0]._id);
          } else {
            this.pdfSrc = null;
          }

          alert("Sucesso: PDF removido com sucesso");
        }
      },
      (error) => {
        this.loading.removingPdf = false;
        alert("Erro: " + (error.error.msg || "Erro ao remover o PDF"));
      }
    );
  }

  uploadFoto(): void {
    if (this.fotoForm.invalid) {
      alert("Atenção: O título da foto é obrigatório");
      return;
    }

    if (!this.selectedFotoFile) {
      alert("Atenção: Selecione uma imagem para upload");
      return;
    }

    const filtro = {
      modulo: this.data.dadosDiagrama.modulo,
      marca: this.data.dadosDiagrama.marca,
      modelo: this.data.dadosDiagrama.modelo,
      ano: this.data.dadosDiagrama.ano,
      motor: this.data.dadosDiagrama.motor,
      sistema_injecao: this.data.dadosDiagrama.sistema_injecao,
      tipo_diagrama: this.data.dadosDiagrama.tipo_diagrama,
    };

    const formData = new FormData();
    formData.append("foto", this.selectedFotoFile);
    formData.append("diagramaId", this.data.diagramaId);
    formData.append("jsonComponenteId", this.data.componente.id);
    formData.append("filtro", JSON.stringify(filtro));
    formData.append("titulo", this.fotoForm.value.titulo);

    this.loading.uploadingFoto = true;

    this.diagramComponentService.uploadFoto(formData).subscribe(
      (response: any) => {
        this.loading.uploadingFoto = false;

        if (response && response.code === 200) {
          this.fotos.push({
            _id: response.foto._id,
            titulo: response.foto.titulo,
          });

          if (!this.componenteInfo.fotoId) {
            this.componenteInfo.fotoId = [];
          }
          this.componenteInfo.fotoId.push(response.foto._id);

          this.fotoForm.reset();
          this.selectedFotoFile = null;

          this.selectFoto(this.fotos.length - 1);

          this.fotoFormOpen = false;

          alert("Sucesso: Foto enviada com sucesso");
        }
      },
      (error) => {
        this.loading.uploadingFoto = false;
        alert("Erro: " + (error.error.msg || "Erro ao enviar a foto"));
      }
    );
  }

  removeFoto(index: number): void {
    if (!this.fotos[index]) return;

    const confirmed = confirm("Deseja realmente excluir esta foto?");

    if (!confirmed) {
      return;
    }

    const fotoId = this.fotos[index]._id;

    this.loading.removingFoto = true;

    this.diagramComponentService.removeFoto(fotoId, this.data.diagramaId, this.data.componente.id).subscribe(
      (response: any) => {
        this.loading.removingFoto = false;

        if (response && response.code === 200) {
          this.fotos.splice(index, 1);

          const fotoIdIndex = this.componenteInfo.fotoId.indexOf(fotoId);
          if (fotoIdIndex > -1) {
            this.componenteInfo.fotoId.splice(fotoIdIndex, 1);
          }

          if (this.fotos.length > 0) {
            this.selectedFotoIndex = 0;
            this.loadFotoContent(this.fotos[0]._id);
          } else {
            this.fotoSrc = null;
          }

          alert("Sucesso: Foto removida com sucesso");
        }
      },
      (error) => {
        this.loading.removingFoto = false;
        alert("Erro: " + (error.error.msg || "Erro ao remover a foto"));
      }
    );
  }
}
